/**
 * The idempotency key every mutating call carries, generated without a secure context.
 *
 * `crypto.randomUUID()` is **secure-context only**. It is present on `localhost` — which
 * the specification treats as secure — and absent over plain HTTP on any other host, where
 * it is not a function that fails but a property that is `undefined`. Several call sites
 * evaluate the key in a `useState` or `useRef` initialiser, so the `TypeError` is thrown
 * during render and React unmounts the tree: a blank page, on the deployed instance only,
 * for a screen that works in development.
 *
 * `crypto.getRandomValues()` carries no such restriction, so the fallback is a v4 UUID
 * assembled from it — the same 122 bits of entropy, from the same generator. `Math.random`
 * is deliberately not a further fallback: these keys are what lets the server recognise a
 * retry as the same submission, and two requests colliding on one would have the second
 * answered with the first's result. A platform without a CSPRNG should fail loudly here
 * rather than issue a key that might repeat.
 *
 * TLS remains the actual fix for the deployment (§7 of `docs/06-deployment-ec2.md`) — this
 * makes the client correct off `localhost` regardless, which is what it should have been.
 */
export function idempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  if (typeof crypto === "undefined" || typeof crypto.getRandomValues !== "function") {
    throw new Error(
      "No cryptographic random source is available, so an idempotency key cannot be issued.",
    );
  }

  // Version 4 in the high nibble of octet 6, variant 10x in the top bits of octet 8 —
  // RFC 9562 §5.4. Without these the value is 128 random bits but not a UUID, and the
  // server's `format: uuid` validation refuses it. Applied through the mapping rather
  // than by writing back into the array so the octets are never read by index.
  const bytes = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte, index) => {
    if (index === 6) return (byte & 0x0f) | 0x40;
    if (index === 8) return (byte & 0x3f) | 0x80;
    return byte;
  });

  const hex = bytes.map((byte) => byte.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
}
